import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Stack, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AuthService } from '../../../services/auth.service';
import { DashboardService } from '../../../services/dashboard.service';
import { useToast } from '../../../context/ToastContext';

const WIRED_KPIS: { key: string; labelKey: 'kpiTargets.onTimeRate' | 'kpiTargets.presentRate' | 'kpiTargets.headcount' }[] = [
    { key: 'attendance.onTimeRate', labelKey: 'kpiTargets.onTimeRate' },
    { key: 'attendance.presentRate', labelKey: 'kpiTargets.presentRate' },
    { key: 'people.headcount', labelKey: 'kpiTargets.headcount' },
];

export default function KpiTargetsSettings() {
    const { t } = useTranslation('dashboard');
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution();
    const [values, setValues] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let live = true;
        DashboardService.getKpiTargets(institutionId)
            .then(d => {
                if (!live) return;
                const map: Record<string, string> = {};
                d.targets.forEach(t => { map[t.kpiKey] = String(t.targetValue); });
                setValues(map);
            })
            .catch(err => toast.showApiError(err, t('kpiTargets.loadFailed')))
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            // The PUT is the full desired set: a blank (or non-positive) field is omitted, which
            // clears that target on the server — this is how a saved target gets removed.
            const targets = WIRED_KPIS
                .filter(k => values[k.key] !== undefined && values[k.key] !== '' && Number(values[k.key]) > 0)
                .map(k => ({ kpiKey: k.key, targetValue: Number(values[k.key]) }));
            await DashboardService.setKpiTargets(institutionId, targets);
            toast.showSuccess(t('kpiTargets.saved'));
        } catch (err) {
            toast.showApiError(err, t('kpiTargets.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 480 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>{t('kpiTargets.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('kpiTargets.intro')}
            </Typography>
            <Paper sx={{ p: 3 }}>
                <Stack spacing={2}>
                    {WIRED_KPIS.map(k => (
                        <TextField
                            key={k.key}
                            label={t(k.labelKey)}
                            type="number"
                            value={values[k.key] ?? ''}
                            onChange={(e) => setValues(prev => ({ ...prev, [k.key]: e.target.value }))}
                            fullWidth
                        />
                    ))}
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? t('kpiTargets.saving') : t('kpiTargets.save')}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
